import { clear, createStore, del, get, set, type UseStore } from 'idb-keyval';

const STORE_NAME = 'pendingCropPictures';

let store: UseStore | undefined;

function getStore(): UseStore {
  if (!store) {
    store = createStore(STORE_NAME, STORE_NAME);
  }
  return store;
}

let warmPromise: Promise<void> | undefined;

/** Opens the IDB store at app boot so the first photo does not pay cold-start cost. */
export function warmPendingCropPicturesStore(): Promise<void> {
  if (!warmPromise) {
    warmPromise = get('__warmup__', getStore())
      .then(() => undefined)
      .catch(() => undefined);
  }
  return warmPromise;
}

export type PendingCropPictureRecord = {
  measureId: number;
  field: string;
  name: string;
  type: string;
  lastModified: number;
  blob: Blob;
};

export function createPendingCropPictureId(
  measureId: number,
  field: string
): string {
  return `${measureId}:${field}:${Date.now()}:${Math.random()
    .toString(36)
    .slice(2, 9)}`;
}

export async function savePendingCropPicture(
  picture: File,
  meta: { measureId: number; field: string },
  pictureId = createPendingCropPictureId(meta.measureId, meta.field)
): Promise<string> {
  const record: PendingCropPictureRecord = {
    measureId: meta.measureId,
    field: meta.field,
    name: picture.name || `crop-picture-${meta.measureId}.jpg`,
    type: picture.type || 'image/jpeg',
    lastModified: picture.lastModified || Date.now(),
    blob: picture,
  };
  await set(pictureId, record, getStore());
  return pictureId;
}

export async function loadPendingCropPictureFile(
  pictureId: string
): Promise<File | null> {
  const record = await get<PendingCropPictureRecord>(pictureId, getStore());
  if (!record?.blob) {
    return null;
  }
  return new File([record.blob], record.name, {
    type: record.type,
    lastModified: record.lastModified,
  });
}

export async function deletePendingCropPicture(
  pictureId: string
): Promise<void> {
  await del(pictureId, getStore());
}

export async function clearPendingCropPictures(): Promise<void> {
  await clear(getStore());
}

/**
 * Ensures the File is durable in IDB and sets `pictureId` on the variables
 * object in place (same reference as mutation.state.variables).
 */
export async function ensurePendingCropPicture(variables: {
  measureId: number;
  field: string;
  picture?: File;
  pictureId?: string;
}): Promise<string | undefined> {
  if (variables.pictureId) {
    return variables.pictureId;
  }
  if (!variables.picture) {
    return undefined;
  }
  const pictureId = await savePendingCropPicture(variables.picture, {
    measureId: variables.measureId,
    field: variables.field,
  });
  variables.pictureId = pictureId;
  return pictureId;
}
